/**
 * ConnectorLibraryTab — Admin → "Connector library", for group admins (their
 * organization and the ones under it) and super admins.
 *
 * Reworked 2026-10-21 (operator decisions on the UX audit): one line per
 * connector in a table; what needs attention (a failed refresh, agents that
 * need their own key) in one strip above it; a plain subtitle with "How
 * sharing works"; one connector opens in a centred window
 * (LibraryEntryDialog) — Settings, Agents, History.
 *
 * Item 83 (Segev 2026-11-02): ONE organization filter (ScopeFilter) decides
 * everything the page shows — one organization, an organization and all
 * under it, or several — and the table lists every connector in them: the
 * ones each added itself (a sub-organization's own included) and those
 * shared to them, once each, with the organization that added it. The
 * "Managing" select and the "Your connectors / Shared with you by" sections
 * are gone; the Organization column and its filter replace them. Every
 * column filters from its heading (ColumnFilters). It opens on the admin's
 * own organization and everything under it, and the sentence under the
 * title says exactly what is listed.
 *
 * What a manager may do is unchanged: each row acts at its `level` — its
 * owner when the owner is shown, else the organization nearest the top of
 * the choice that gets it — with today's rules; a change never reaches the
 * levels above. Every destructive action asks the server what it would do
 * first and says so.
 *
 * The tenant list is passed in, as for SystemDesignTab: AdminPage already
 * holds exactly the organizations this admin manages.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Dropdown, Select, Tooltip, type MenuProps } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import { useAuth } from '@hooks/useAuth';
import { useFeature } from '@hooks/useFeature';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { showNotification } from '@/utils/showNotification';
import { mirrorPlacement } from '@/utils/rtl';
import { bidiIsolate } from '@/utils/bidi';
import type { SignInValue } from '@modules/chatbots/utils/signIn';
import type { TenantHierarchy } from '@/types/admin';
import { Field, PlusGlyph } from '@modules/chatbots/components/ConnectorDialog';
import {
  connectorLibraryService,
  errorBody,
  type ChangeOutcome,
  type ChangePreview,
  type LibraryEntryEditable,
  type LibraryScopePage,
} from '../services/connectorLibraryService';
import {
  agentsCell, ariaSort, attention, columnChoices, filterRows, nextSort, noColumnFilters, organizationsCell,
  ownerChoices, reachOf, reachWords, restoreTarget as restoreTo, rowStatus, signIn, sortByCount, statusOf,
  type ColumnFilters, type CountSort, type Kind, type Reach, type SignIn, type StatusKey, type UseFilter,
} from '../utils/libraryList';
import { defaultPicks, pickWords, scopeIds, scopeParams, scopeWords, type Pick } from '../utils/scope';
import { buildTree, flatten, type TreeOrg } from '../utils/orgTree';
import { KIND_LABEL, KindTag, LibraryGlyph, MoreGlyph, ServiceIcon, SharingHelp, WarnGlyph } from './LibraryBits';
import { AddConnectorDialog } from './AddConnectorDialog';
import { ScopeFilter } from './ScopeFilter';
import { Choices, FilterChips, FilterHeading, FiltersSheet, OneOf, type ColumnFilter } from './ColumnFilters';
import { LibraryEntryDialog, StatusPill, type EntryTab } from './LibraryEntryDialog';
import { outcomeText } from './LibraryPanels';
import '@modules/chatbots/components/ConnectorDialog.css';
import '@modules/chatbots/components/BotConnectorsTab.css';
import './ConnectorLibrary.css';

interface Props {
  /** Every organization the signed-in admin manages (own + descendants). */
  tenants: TenantHierarchy[];
}

type Row = LibraryScopePage['entries'][number];
type Pending = { row: Row; action: 'remove' | 'restore'; preview: ChangePreview } | null;
type Open = { entryId: string; tab: EntryTab; startSignIn?: SignInValue } | null;

const SIGN_INS: SignIn[] = ['none', 'each', 'shared', 'person', 'personKey'];
const REACHES: Reach[] = ['all', 'some', 'removed'];
const STATUSES: StatusKey[] = ['ok', 'failed', 'signin', 'changed', 'hidden'];

export default function ConnectorLibraryTab({ tenants }: Props) {
  const { t, direction } = useLanguage();
  const { tenantId: ownTenantId } = useAuth();
  const [params] = useSearchParams();
  const phone = useMediaQuery('(max-width: 768px)');
  // The organizations the page shows (ScopeFilter); null until the tree is known.
  const [picks, setPicks] = useState<Pick[] | null>(null);
  const [page, setPage] = useState<LibraryScopePage | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<ColumnFilters>(noColumnFilters);
  const [sort, setSort] = useState<CountSort>(null);
  // Add is open; `start` is the kind the empty state's suggestion picked; `addTo` the organization it goes to.
  const [adding, setAdding] = useState(false);
  const [addStart, setAddStart] = useState<'starter' | 'mcp' | 'api' | undefined>(undefined);
  const [addTo, setAddTo] = useState<string | null>(null);
  const [open, setOpen] = useState<Open>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // The kinds this organization has switched on — known before the page
  // loads, so "Add connector" is there from the first paint.
  const mcpOn = useFeature('integrations.mcp_connectors');
  const apiOn = useFeature('integrations.api_connectors');

  const orgs: TreeOrg[] = useMemo(
    () => tenants.map(x => ({ tenant_id: x.tenant_id, parent_id: x.parent_tenant_id ?? null, name: x.tenant_name })),
    [tenants]);

  // Start on the organization the link asked for (the agent editor's "Manage
  // list") and everything under it, else the admin's own and everything under it.
  useEffect(() => {
    if (picks || orgs.length === 0) return;
    const asked = params.get('org');
    setPicks(orgs.some(o => o.tenant_id === asked) ? [{ id: asked!, below: true }] : defaultPicks(orgs, ownTenantId));
  }, [orgs, ownTenantId, params, picks]);

  const latest = useRef(0);
  const load = useCallback(async () => {
    if (!picks || picks.length === 0) return;
    // Only the latest request may write: choosing quickly must never leave
    // one choice's list on screen under another's sentence and actions.
    const request = ++latest.current;
    setLoadError(false);
    try {
      const next = await connectorLibraryService.scopePage(scopeParams(picks));
      if (request === latest.current) setPage(next);
    } catch {
      if (request === latest.current) setLoadError(true);
    }
  }, [picks]);

  useEffect(() => { void load(); }, [load]);
  // What an action reloads with: the page as it is chosen NOW.
  const loadNow = useRef(load);
  useEffect(() => { loadNow.current = load; }, [load]);
  const reload = useCallback(() => loadNow.current(), []);

  const choose = (next: Pick[]) => {
    setPicks(next);
    setPage(null);
    setOpen(null);
    // The organizations the Organization column offered belong to the old choice.
    setFilters(f => ({ ...f, owner: new Set() }));
  };

  const kinds = useMemo(
    () => page?.kinds ?? [...(mcpOn ? ['mcp' as const] : []), ...(apiOn ? ['api' as const] : [])],
    [page, mcpOn, apiOn]);
  const rows = useMemo(() => page?.entries ?? [], [page]);
  const shown = useMemo(() => sortByCount(filterRows(rows, query, filters), sort), [rows, query, filters, sort]);
  const needs = useMemo(() => attention(rows), [rows]);

  // The sentence under the title: what the server listed, in the tree's order.
  const scope = useMemo(() => (page
    ? scopeWords(pickWords(orgs, page.scope.picks.map(p => ({ id: p.tenant_id, below: p.below }))), t) : ''),
  [page, orgs, t]);

  const openRow = open ? rows.find(r => r.entry_id === open.entryId) ?? null : null;
  /** The organization a row acts at. */
  const levelOf = (row: Row) => ({ id: row.level.tenant_id, name: row.level.name ?? '', below: row.level.descendant_count });

  // ── Add: to which organization ───────────────────────────────────────
  const onlyOne = page && page.scope.picks.length === 1 ? page.scope.picks[0].tenant_id : null;
  const openAdd = (start?: 'starter' | 'mcp' | 'api') => {
    setAddStart(start);
    setAddTo(onlyOne);
    setAdding(true);
  };
  // Offered: the organizations shown, as their tree — so what is added is listed, and the
  // duplicate check below sees everything that organization's list holds.
  const addNodes = useMemo(() => {
    const shown = page ? scopeIds(orgs, page.scope.picks.map(p => ({ id: p.tenant_id, below: p.below }))) : new Set();
    return flatten(buildTree(orgs)).filter(n => shown.has(n.org.tenant_id));
  }, [orgs, page]);
  const addNode = addNodes.find(n => n.org.tenant_id === addTo) ?? null;
  // What the chosen organization's list already holds (its own and what is shared down to it).
  const addExisting = useMemo(() => {
    if (!addTo) return [];
    const above = ancestors(tenants, addTo);
    return rows.filter(r => !r.removed_here && (r.owner.tenant_id === addTo || above.has(r.owner.tenant_id)))
      .map(r => ({ ...r, inherited: r.owner.tenant_id !== addTo, owner_name: r.owner.name }));
  }, [rows, tenants, addTo]);

  const toast = (message: string, outcome?: ChangeOutcome | null) =>
    showNotification(outcome ? `${message} ${outcomeText(t, outcome)}.` : message, 'success');

  const act = async (row: Row, fn: () => Promise<void>, failKey: string) => {
    setBusyId(row.entry_id);
    try {
      await fn();
    } catch (err) {
      const body = errorBody(err);
      // A failed refresh is recorded on the entry ("Refresh failed").
      if (body.entry) void reload();
      showNotification(body.error || t(failKey), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const refresh = (row: Row) => act(row, async () => {
    const { outcome } = await connectorLibraryService.refresh(row.entry_id, levelOf(row).id);
    const off = outcome.new_tools_off || [];
    toast(t('connectorLibrary.refreshed', { name: row.name })
      + (off.length ? ` ${t('connectorLibrary.newToolsOff', { count: off.length, names: off.join(', ') })}` : ''), outcome);
    await reload();
  }, 'connectorLibrary.errors.refresh');

  /** Every destructive action asks the server what it would do, then says so. */
  const ask = (row: Row, action: 'remove' | 'restore') => act(row, async () => {
    const preview = await connectorLibraryService.preview(row.entry_id, levelOf(row).id, action);
    setPending({ row, action, preview });
  }, 'connectorLibrary.errors.preview');

  const confirmPending = () => {
    const p = pending;
    if (!p) return;
    setPending(null);
    void act(p.row, async () => {
      if (p.action === 'remove') {
        const outcome = await connectorLibraryService.remove(p.row.entry_id, levelOf(p.row).id);
        toast(p.row.inherited
          ? t('connectorLibrary.removedHere', { name: p.row.name })
          : t('connectorLibrary.deleted', { name: p.row.name }), outcome);
        if (!p.row.inherited) setOpen(null);   // deleted: nothing left to show
      } else {
        const outcome = await connectorLibraryService.restore(p.row.entry_id, levelOf(p.row).id);
        toast(t('connectorLibrary.restored', { name: p.row.name, owner: restoreTo(p.row) }), outcome);
      }
      await reload();
    }, p.action === 'remove' ? 'connectorLibrary.errors.remove' : 'connectorLibrary.errors.restore');
  };

  const menuFor = (row: Row): MenuProps['items'] => {
    const items: MenuProps['items'] = [];
    if (row.removed_here) {
      items.push({ key: 'restore', label: t('connectorLibrary.menu.unhide'), onClick: () => { void ask(row, 'restore'); } });
    } else {
      items.push({ key: 'open', label: t('connectorLibrary.menu.open'),
                   onClick: () => setOpen({ entryId: row.entry_id, tab: 'settings' }) });
      // An MCP server that needs a key the list does not keep cannot be
      // re-listed from here; an API entry is re-read from its document URL.
      if (!row.inherited && (row.kind === 'mcp' ? !row.needs_credential : !!row.spec_url)) {
        items.push({
          key: 'refresh',
          label: row.kind === 'mcp' ? t('connectorLibrary.menu.refreshTools') : t('connectorLibrary.menu.rereadDocument'),
          onClick: () => { void refresh(row); },
        });
      }
    }
    items.push({ key: 'agents', label: t('connectorLibrary.menu.agents'), onClick: () => setOpen({ entryId: row.entry_id, tab: 'agents' }) });
    items.push({ key: 'history', label: t('connectorLibrary.menu.history'), onClick: () => setOpen({ entryId: row.entry_id, tab: 'history' }) });
    if (row.inherited && row.edited_here && !row.removed_here) {
      items.push({ key: 'restore', label: t('connectorLibrary.menu.restore', { owner: restoreTo(row) }), onClick: () => { void ask(row, 'restore'); } });
    }
    if (!row.removed_here) {
      items.push({ type: 'divider' });
      items.push({
        key: 'remove',
        danger: true,
        label: row.inherited ? t('connectorLibrary.menu.removeHere') : t('connectorLibrary.menu.delete'),
        onClick: () => { void ask(row, 'remove'); },
      });
    }
    return items;
  };

  const onSaved = (entry: LibraryEntryEditable, outcome: ChangeOutcome | null) => {
    toast(t('connectorLibrary.saved', { name: entry.name }), outcome);
    void reload();
  };

  const onAdded = async (entry: LibraryEntryEditable, finishSignIn?: SignInValue) => {
    setAdding(false);
    toast(t('connectorLibrary.added', { name: entry.name }));
    await reload();
    // Each person signs in (item 65A): nothing to set until a manager signs
    // in and lists its tools — that is where it opens; so does a sign-in the
    // add window left to finish there (a REST API's sign-in addresses, item 80).
    setOpen({ entryId: entry.entry_id, tab: entry.status === 'sign_in_needed' || finishSignIn ? 'signin' : 'settings',
              startSignIn: finishSignIn });
  };

  // What the pending confirmation says — the server's own count.
  const confirmCopy = (() => {
    if (!pending) return null;
    const { row, action, preview } = pending;
    const owner = row.owner_name ?? '';
    // Said of the level the row acts at.
    const { name: organization, below } = levelOf(row);
    if (action === 'restore') {
      return {
        title: row.removed_here ? t('connectorLibrary.restore.unhideTitle', { name: row.name })
          : t('connectorLibrary.restore.title', { name: row.name, owner: restoreTo(row) }),
        message: `${row.removed_here
          ? t('connectorLibrary.restore.bodyRemoved', { organization, count: below })
          : t('connectorLibrary.restore.bodyEdited', { organization, count: below })} ${
          t('connectorLibrary.restore.updates', { count: preview.updates, owner: restoreTo(row) })}`,
        confirm: row.removed_here ? t('connectorLibrary.menu.unhide') : t('connectorLibrary.restore.confirm'),
        variant: 'warning' as const,
      };
    }
    // Who loses it, by the server's own count: agents and their organizations.
    const detaches = preview.detaches > 0
      ? t('connectorLibrary.remove.loses', {
        agents: t('connectorLibrary.orgs.agentsCount', { count: preview.detaches }),
        organizations: t('connectorLibrary.orgs.orgsCount', { count: preview.detached_organizations }) })
      : t('connectorLibrary.remove.detaches', { count: 0 });
    return row.inherited ? {
      title: t('connectorLibrary.remove.layerTitle', { name: row.name }),
      message: `${t('connectorLibrary.remove.layerBody', { owner, organization, count: below })} ${detaches}`,
      confirm: t('connectorLibrary.remove.layerConfirm'),
      variant: 'danger' as const,
    } : {
      title: t('connectorLibrary.remove.ownTitle', { name: row.name }),
      message: `${t('connectorLibrary.remove.ownBody', { organization, count: below })} ${detaches}`,
      confirm: t('connectorLibrary.remove.ownConfirm'),
      variant: 'danger' as const,
    };
  })();

  // ── The column filters: one per column, each from its heading ─────────
  const set = <K extends keyof ColumnFilters>(key: K, value: ColumnFilters[K]) => setFilters(f => ({ ...f, [key]: value }));
  const owners = useMemo(() => ownerChoices(rows), [rows]);
  const signInLabel = (v: SignIn) => t(`connectorLibrary.signIn.${v}`);
  const reachLabel = (v: Reach) => t(`connectorLibrary.colFilter.reach.${v}`);
  const statusLabel = (v: StatusKey) => t(`connectorLibrary.colFilter.status.${v}`);
  const useLabel = (v: UseFilter) => (v === 'used' ? t('connectorLibrary.filterUsed') : v === 'unused'
    ? t('connectorLibrary.filterUnused') : t('connectorLibrary.filterAll'));
  const inOrder = <V extends string>(order: V[], choices: { value: V; count: number }[]) =>
    order.map(v => choices.find(c => c.value === v)).filter((c): c is { value: V; count: number } => !!c);

  const columns: Record<'owner' | 'kind' | 'signIn' | 'reach' | 'use' | 'status', ColumnFilter> = {
    owner: {
      key: 'owner', heading: t('connectorLibrary.col.organization'), active: filters.owner.size > 0,
      panel: <Choices title={t('connectorLibrary.colFilter.ownerTitle')} searchable selected={filters.owner}
                      onChange={v => set('owner', v)}
                      choices={owners.map(o => ({ value: o.id, text: o.name, count: o.count,
                                                  view: <bdi dir="auto">{o.name}</bdi> }))} />,
    },
    kind: {
      key: 'kind', heading: t('connectorLibrary.col.type'), active: filters.kind.size > 0,
      panel: <Choices<Kind> title={t('connectorLibrary.col.type')} selected={filters.kind} onChange={v => set('kind', v)}
                            choices={inOrder<Kind>(['mcp', 'api'], columnChoices(rows, r => r.kind))
                              .map(c => ({ ...c, text: KIND_LABEL[c.value], view: KIND_LABEL[c.value] }))} />,
    },
    signIn: {
      key: 'signIn', heading: t('connectorLibrary.col.signIn'), active: filters.signIn.size > 0,
      panel: <Choices<SignIn> title={t('connectorLibrary.col.signIn')} selected={filters.signIn}
                              onChange={v => set('signIn', v)}
                              choices={inOrder(SIGN_INS, columnChoices(rows, r => signIn(r)))
                                .map(c => ({ ...c, text: signInLabel(c.value), view: signInLabel(c.value) }))} />,
    },
    reach: {
      key: 'reach', heading: t('connectorLibrary.col.availableTo'), active: filters.reach.size > 0,
      panel: <Choices<Reach> title={t('connectorLibrary.col.availableTo')} selected={filters.reach}
                             onChange={v => set('reach', v)}
                             choices={inOrder(REACHES, columnChoices(rows, r => reachOf(r)))
                               .map(c => ({ ...c, text: reachLabel(c.value), view: reachLabel(c.value) }))} />,
    },
    use: {
      key: 'use', heading: t('connectorLibrary.col.agentsUsing'), active: filters.use !== 'all',
      panel: <OneOf<UseFilter> title={t('connectorLibrary.col.agentsUsing')} value={filters.use}
                               onChange={v => set('use', v)}
                               options={(['all', 'used', 'unused'] as UseFilter[]).map(v => ({ value: v, label: useLabel(v) }))} />,
    },
    status: {
      key: 'status', heading: t('connectorLibrary.col.status'), active: filters.status.size > 0,
      panel: <Choices<StatusKey> title={t('connectorLibrary.col.status')} selected={filters.status}
                                 onChange={v => set('status', v)}
                                 choices={inOrder(STATUSES, columnChoices(rows, r => statusOf(r)))
                                   .map(c => ({ ...c, text: statusLabel(c.value), view: statusLabel(c.value) }))} />,
    },
  };

  // The chips: one per column in force, "Column: a, b".
  const chip = (key: keyof ColumnFilters, values: string[]) => ({
    key,
    label: columns[key].heading,
    text: <>{columns[key].heading}: <bdi dir="auto">{values.join(', ')}</bdi></>,
    onRemove: () => set(key, noColumnFilters()[key]),
  });
  const chips = [
    ...(filters.owner.size ? [chip('owner', owners.filter(o => filters.owner.has(o.id)).map(o => o.name))] : []),
    ...(filters.kind.size ? [chip('kind', [...filters.kind].map(k => KIND_LABEL[k]))] : []),
    ...(filters.signIn.size ? [chip('signIn', [...filters.signIn].map(signInLabel))] : []),
    ...(filters.reach.size ? [chip('reach', [...filters.reach].map(reachLabel))] : []),
    ...(filters.use !== 'all' ? [chip('use', [useLabel(filters.use)])] : []),
    ...(filters.status.size ? [chip('status', [...filters.status].map(statusLabel))] : []),
  ];

  const narrowed = !!query.trim() || chips.length > 0;
  const canAdd = kinds.length > 0;
  const subtitle = !page ? t('common.loading')
    : narrowed ? t('connectorLibrary.scope.showingFiltered', { count: shown.length, total: rows.length, scope })
      : t('connectorLibrary.scope.showing', { count: rows.length, scope });

  return (
    <div className="cxl-page" data-testid="connector-library-page">
      <div className="cxl-page__head">
        <div className="cxl-page__heading">
          <h2 className="cxl-page__title">{t('connectorLibrary.title')}</h2>
          <p className="cxl-page__subtitle" data-testid="connector-library-sentence">
            {subtitle}{' '}
            {page && <SharingHelp />}
          </p>
        </div>
        <div className="cxl-page__actions">
          {canAdd && (
            <button type="button" className="cx-btn cx-btn--primary cxl-page__add" onClick={() => openAdd()}
                    disabled={!page} data-testid="connector-library-add">
              <PlusGlyph />
              {t('connectorLibrary.add.button')}
            </button>
          )}
        </div>
      </div>

      {needs.length > 0 && (
        <div className="cxl-attention" role="status" data-testid="connector-library-attention">
          <span className="cxl-attention__icon" aria-hidden="true"><WarnGlyph /></span>
          <span className="cxl-attention__title">{t('connectorLibrary.attention.title', { count: needs.length })}</span>
          <span className="cxl-attention__items">
            {needs.map(item => (
              <button key={`${item.kind}-${item.row.entry_id}`} type="button" className="cxl-attention__item"
                      onClick={() => setOpen({ entryId: item.row.entry_id, tab: item.kind === 'needs_key' ? 'agents' : 'settings' })}
                      data-testid={`connector-library-attention-${item.kind}-${item.row.slug}`}>
                <span dir="auto">{item.row.name}</span>
                {' — '}
                {item.kind === 'failed'
                  ? (item.row.kind === 'mcp' ? t('connectorLibrary.attention.refreshFailed') : t('connectorLibrary.attention.readFailed'))
                  : t('connectorLibrary.attention.needsKey', { count: item.count })}
              </button>
            ))}
          </span>
        </div>
      )}

      <div className="cxl-tools">
        {picks && <ScopeFilter orgs={orgs} value={picks} onChange={choose} ownId={ownTenantId ?? null} />}
        {rows.length > 0 && (
          <input
            className="form-input cxl-tools__search"
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('connectorLibrary.search')}
            aria-label={t('connectorLibrary.search')}
            dir="auto"
            data-testid="connector-library-page-search"
          />
        )}
        {phone && rows.length > 0 && <FiltersSheet filters={Object.values(columns)} count={chips.length} />}
      </div>
      <FilterChips chips={chips} onClearAll={() => setFilters(noColumnFilters())} />

      {!page && !loadError ? (
        <div className="cxl-table cxl-table--skeleton" aria-busy="true" data-testid="connector-library-loading">
          {[0, 1, 2, 3].map(i => <div key={i} className="cxl-row cxl-row--skeleton" />)}
        </div>
      ) : loadError ? (
        <div className="cxl-empty" role="alert" data-testid="connector-library-error">
          <p className="cxl-empty__title">{t('connectorLibrary.errors.loadList')}</p>
          <button type="button" className="cx-btn" onClick={() => void load()}>{t('common.retry')}</button>
        </div>
      ) : page && rows.length === 0 ? (
        <div className="cxl-empty" data-testid="connector-library-empty">
          <span className="cxl-empty__icon" aria-hidden="true"><LibraryGlyph /></span>
          <p className="cxl-empty__title">{t('connectorLibrary.empty.title')}</p>
          <p className="cxl-empty__hint">{t('connectorLibrary.scope.emptyHint', { scope })}</p>
          {canAdd && (
            <div className="cxl-empty__ways">
              {/* Each way opens Add already on it. */}
              <button type="button" className="cxl-empty__way" onClick={() => openAdd('starter')}
                      data-testid="connector-library-empty-way-starter">
                {t('connectorLibrary.empty.starters')}
              </button>
              {kinds.includes('mcp') && (
                <button type="button" className="cxl-empty__way" onClick={() => openAdd('mcp')}
                        data-testid="connector-library-empty-way-mcp">
                  {t('connectorLibrary.empty.mcp')}
                </button>
              )}
              {kinds.includes('api') && (
                <button type="button" className="cxl-empty__way" onClick={() => openAdd('api')}
                        data-testid="connector-library-empty-way-api">
                  {t('connectorLibrary.empty.api')}
                </button>
              )}
              <button type="button" className="cx-btn cx-btn--primary" onClick={() => openAdd()}
                      data-testid="connector-library-empty-add">
                <PlusGlyph />
                {t('connectorLibrary.add.button')}
              </button>
            </div>
          )}
        </div>
      ) : page && (
        <>
          {narrowed && shown.length === 0 && <p className="cxl-nomatch">{t('connectorLibrary.noMatch')}</p>}
          <div className="cxl-table" role="table" aria-label={subtitle}>
            <div className="cxl-row cxl-row--head" role="row">
              <span role="columnheader">{t('connectorLibrary.col.connector')}</span>
              <span role="columnheader"><FilterHeading filter={columns.owner} /></span>
              <span role="columnheader"><FilterHeading filter={columns.kind} /></span>
              <span role="columnheader"><FilterHeading filter={columns.signIn} /></span>
              <span role="columnheader">
                <FilterHeading filter={columns.reach}>
                  <Tooltip placement="top" trigger={['hover', 'focus']} title={t('connectorLibrary.col.availableToTip')}>
                    <span className="cxl-col-tip" data-testid="connector-library-available-tip">
                      {t('connectorLibrary.col.availableTo')}
                    </span>
                  </Tooltip>
                </FilterHeading>
              </span>
              <span role="columnheader" className="cxl-row__count cxl-colh-pair" aria-sort={ariaSort(sort, 'agents')}>
                <button type="button" className="cxl-sort" onClick={() => setSort(prev => nextSort(prev, 'agents'))}
                        title={t('connectorLibrary.col.agentsSort')} data-testid="connector-library-sort-agents">
                  {t('connectorLibrary.col.agentsUsing')}
                  <SortGlyph sort={sort?.by === 'agents' ? sort.dir : 'none'} />
                </button>
                <FilterHeading filter={columns.use}>
                  <span className="cxl-row__sr">{columns.use.heading}</span>
                </FilterHeading>
              </span>
              <span role="columnheader"><FilterHeading filter={columns.status} /></span>
              <span role="columnheader" className="cxl-row__sr">{t('connectorLibrary.col.actions')}</span>
            </div>
            {shown.map(row => (
              <LibraryRow key={row.entry_id} row={row} busy={busyId === row.entry_id} menu={menuFor(row)}
                          onReach={() => setOpen({ entryId: row.entry_id, tab: 'organizations' })}
                          selected={open?.entryId === row.entry_id} direction={direction}
                          onOpen={() => setOpen({ entryId: row.entry_id, tab: row.removed_here ? 'agents' : 'settings' })}
                          onRestore={() => { void ask(row, 'restore'); }} />
            ))}
          </div>
        </>
      )}

      {adding && (
        <AddConnectorDialog tenantId={addTo} organizationName={addNode?.org.name ?? ''}
                            descendantCount={addNode ? addNode.ids.length - 1 : 0}
                            kinds={kinds} starters existing={addExisting} start={addStart}
                            organizationField={addNodes.length < 2 ? undefined : (
                              <Field label={t('connectorLibrary.add.toWhich')} hint={t('connectorLibrary.add.toWhichHint')}>
                                <Select className="cxl-scope__select" value={addTo ?? undefined} onChange={(v: string) => setAddTo(v)}
                                        placeholder={t('connectorLibrary.add.toWhichPlaceholder')} showSearch optionFilterProp="name"
                                        popupMatchSelectWidth={false} data-testid="connector-library-add-org"
                                        aria-label={t('connectorLibrary.add.toWhich')}
                                        options={addNodes.map(n => ({
                                          value: n.org.tenant_id,
                                          // Searched by `name`: the label is markup.
                                          name: n.org.name,
                                          label: <span className="cxl-scope__opt" data-level={Math.min(n.depth, 5)}><bdi dir="auto">{n.org.name}</bdi></span>,
                                        }))} />
                              </Field>
                            )}
                            onClose={() => setAdding(false)}
                            onAdded={(entry, finishSignIn) => { void onAdded(entry, finishSignIn); }} />
      )}
      {openRow && open && (
        <LibraryEntryDialog tenantId={levelOf(openRow).id} organizationName={levelOf(openRow).name}
                            descendantCount={levelOf(openRow).below} row={openRow}
                            tab={open.tab} onTab={tab => setOpen({ entryId: openRow.entry_id, tab })}
                            menu={(menuFor(openRow) ?? []).filter(item => item && item.key !== 'open' && item.key !== 'agents' && item.key !== 'history')}
                            onRestore={() => { void ask(openRow, 'restore'); }} busy={busyId === openRow.entry_id}
                            onClose={() => setOpen(null)} onSaved={onSaved} onReachChanged={() => { void reload(); }}
                            startSignIn={open.startSignIn} />
      )}
      <ConfirmModal
        isOpen={!!pending}
        onClose={() => setPending(null)}
        onConfirm={confirmPending}
        title={confirmCopy?.title ?? ''}
        message={confirmCopy?.message ?? ''}
        confirmText={confirmCopy?.confirm}
        cancelText={t('common.cancel')}
        variant={confirmCopy?.variant ?? 'danger'}
      />
    </div>
  );
}

/** The organizations above `id` in the managed tree. */
function ancestors(tenants: TenantHierarchy[], id: string): Set<string> {
  const parent = new Map(tenants.map(x => [x.tenant_id, x.parent_tenant_id ?? null]));
  const out = new Set<string>();
  for (let p = parent.get(id) ?? null; p && !out.has(p); p = parent.get(p) ?? null) out.add(p);
  return out;
}

function LibraryRow({ row, busy, menu, selected, direction, onReach, onOpen, onRestore }: {
  row: Row; busy: boolean; menu: MenuProps['items']; selected: boolean; direction: 'ltr' | 'rtl';
  /** Opens the entry on its Organizations tab. */
  onReach: () => void;
  onOpen: () => void; onRestore: () => void;
}) {
  const { t } = useLanguage();
  const status = rowStatus(row);
  const reach = reachWords(row);
  const agents = agentsCell(row.usage);
  const orgs = organizationsCell(row.usage);
  const more = 'key' in orgs || orgs.more === 0 ? null : t('connectorLibrary.usage.more', { count: orgs.more });
  const sign = signIn(row);
  const owner = (
    <>
      <bdi dir="auto" className="cxl-row__owner-name">{row.owner.name}</bdi>
      {row.owner.parent_name && (
        <span className="cxl-row__owner-parent">{t('connectorLibrary.owner.under', { name: bidiIsolate(row.owner.parent_name) })}</span>
      )}
    </>
  );
  return (
    <div className={`cxl-row ${row.removed_here ? 'is-hidden' : ''} ${selected ? 'is-selected' : ''} ${busy ? 'is-busy' : ''}`}
         role="row" data-testid={`connector-library-row-${row.slug}`} data-owner={row.owner.tenant_id}
         data-level={row.level.tenant_id}>
      <button type="button" className="cxl-row__open" role="cell" onClick={onOpen}
              aria-label={`${t('connectorLibrary.menu.open')}: ${row.name}`}>
        <ServiceIcon name={row.name} slug={row.slug} url={row.kind === 'mcp' ? row.url : row.base_url} />
        <span className="cxl-row__titles">
          {/* dir="auto" on the line itself, so a long one is cut at its END in
              either language; it lines up with the page because the button
              inherits the page's alignment (ConnectorLibrary.css). */}
          <span className="cxl-row__name" dir="auto" title={row.name}>{row.name}</span>
          <span className="cxl-row__desc" dir="auto" title={row.description || undefined}>
            {row.description || (row.kind === 'mcp' ? row.url : row.base_url)}
          </span>
          {/* A phone folds the Organization column: who added it goes under the name. */}
          <span className="cxl-row__owner cxl-row__owner--inline">{owner}</span>
        </span>
      </button>
      <span role="cell" className="cxl-row__meta cxl-row__owner" data-testid={`connector-library-owner-${row.slug}`}>
        {owner}
      </span>
      <span role="cell"><KindTag kind={row.kind} /></span>
      <span role="cell" className="cxl-row__meta" data-testid={`connector-library-signin-${row.slug}`}>
        {t(`connectorLibrary.signIn.${sign}`)}
      </span>
      <span role="cell" className="cxl-row__meta">
        {row.removed_here ? t(reach.key) : (
          <button type="button" className="cxl-row__link cxl-row__reach" onClick={onReach}
                  title={t('connectorLibrary.reach.tip')} data-testid={`connector-library-reach-${row.slug}`}>
            {t(reach.key, reach.values)}
          </button>
        )}
      </span>
      <span role="cell" className={`cxl-row__meta cxl-row__count ${'key' in agents ? 'is-none' : ''}`}
            title={row.usage.customized > 0 ? t('connectorLibrary.usage.customized', { count: row.usage.customized }) : undefined}
            data-testid={`connector-library-used-${row.slug}`}>
        {'key' in agents || 'key' in orgs ? t('connectorLibrary.usage.notUsed') : (
          // "12 · in 4 organizations"; hover, focus or a tap names them.
          <Tooltip placement={mirrorPlacement(direction, 'topRight')} trigger={['hover', 'focus']}
                   overlayClassName="cxl-orgs-tip"
                   title={(
                     <div className="cxl-orgs-tip__body" data-testid={`connector-library-orgs-tip-${row.slug}`}>
                       <p className="cxl-orgs-tip__title">{t('connectorLibrary.usage.usedIn')}</p>
                       <ul className="cxl-orgs-tip__list">
                         {orgs.names.map((name, i) => <li key={i}><bdi dir="auto">{name}</bdi></li>)}
                       </ul>
                       {more && <p className="cxl-orgs-tip__more">{more}</p>}
                     </div>
                   )}>
            <button type="button" className="cxl-row__link cxl-row__orgs"
                    aria-label={`${t('connectorLibrary.orgs.agentsCount', { count: agents.count })}, ${t(
                      'connectorLibrary.usage.orgsLabel', { count: orgs.count,
                                                            names: [...orgs.names, ...(more ? [more] : [])].join(', ') })}`}>
              <bdi dir="ltr" className="cxl-row__agents">{agents.count}</bdi>
              <span className="cxl-row__in"> · {t('connectorLibrary.usage.inOrgs', { count: orgs.count })}</span>
            </button>
          </Tooltip>
        )}
      </span>
      <span role="cell" className="cxl-row__status">
        {status && <StatusPill status={status} />}
        {row.removed_here && (
          <button type="button" className="cxl-row__link" disabled={busy} onClick={onRestore}
                  data-testid={`connector-library-restore-${row.slug}`}>
            {t('connectorLibrary.menu.unhide')}
          </button>
        )}
      </span>
      <span role="cell" className="cxl-row__end">
        <Dropdown menu={{ items: menu }} overlayClassName="cxl-menu" trigger={['click']} placement={mirrorPlacement(direction, 'bottomRight')}>
          <button type="button" className="cxl-icon-btn" aria-label={t('connectorLibrary.menu.label', { name: row.name })}
                  disabled={busy} data-testid={`connector-library-menu-${row.slug}`}>
            <MoreGlyph />
          </button>
        </Dropdown>
      </span>
    </div>
  );
}

/** A count heading's order mark: both ways while unsorted, else the one in force. */
function SortGlyph({ sort }: { sort: 'none' | 'desc' | 'asc' }) {
  return (
    <svg className="cxl-sort__glyph" width="10" height="12" viewBox="0 0 10 12" aria-hidden="true">
      <path d="M5 1 L8.5 5 H1.5 Z" fill="currentColor" opacity={sort === 'desc' ? 0.25 : 1} />
      <path d="M5 11 L1.5 7 H8.5 Z" fill="currentColor" opacity={sort === 'asc' ? 0.25 : 1} />
    </svg>
  );
}
